import { Autocomplete, Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import Textfield from '../../components/input/Textfield';
import * as Yup from 'yup';
import { BASE_URL, apiRequest } from '../../utils/api';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchCustomerData } from '../../redux/slices/customerSlice';
import { fetchRepsData } from '../../redux/slices/repSlice';
import { fetchBankData } from '../../redux/slices/bankSlice';
import { fetchVendorData } from '../../redux/slices/vendorSlice';
import axios from 'axios';
import moment from 'moment';

const Edit = ({ payment, onClose }) => {
    const dispatch = useDispatch();
    let { user } = useSelector((state) => state?.user);
    const [loading, setLoading] = useState(false);

    const { data: reps, isLoading: isLoadingReps, error: errorReps } = useSelector((state) => state.rep);
    const { data: customer, isLoading: isLoadingCustomer, error: errorCustomer } = useSelector((state) => state.customer);
    const { data: bank, isLoading: isLoadingBank, error: errorBank } = useSelector((state) => state.bank);
    const { data: vendor, isLoading: isLoadingVendor, error: errorVendor } = useSelector((state) => state.vendor);

    const [selectedRep, setSelectedRep] = React.useState('');
    const [selectedCustomer, setSelectedCustomer] = React.useState('');
    const [selectedBank, setSelectedBank] = React.useState('');
    const [selectedVendor, setSelectedVendor] = React.useState('');

    const [createdOn, setCreatedOn] = useState(moment(payment?.createdOn).format('YYYY-MM-DDTHH:mm'));
    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
    const [verifiedAt, setVerifiedAt] = useState(
        payment?.verifiedAt ? moment(payment?.verifiedAt).format('YYYY-MM-DD') : today
    );

    useEffect(() => {
        if (!reps) {
            dispatch(fetchRepsData());
        }
        if (!customer) {
            dispatch(fetchCustomerData());
        }
        if (!bank) {
            dispatch(fetchBankData());
        }
        if (!vendor) {
            dispatch(fetchVendorData());
        }

        setSelectedBank(payment?.bank?._id);
        setSelectedVendor(payment?.vendor?._id);
        setSelectedRep(payment?.user?._id);
        setSelectedCustomer(payment?.customer?._id);

        return () => {
            setSelectedRep(null);
            setSelectedBank(null);
            setSelectedVendor(null);
            setSelectedCustomer(null);
        };
    }, []);

    const INITIAL_FORM_STATE = {
        amount: payment?.amount || '',
        comments: payment?.comments || '',
        refNo: payment?.refNo || ''
    };

    const FORM_VALIDATION = Yup.object().shape({
        amount: Yup.number().typeError('Amount must be a number')
    });

    const [uploadedFile, setUploadedFile] = useState(null);

    const handleOnChangeUploadReport = async (file) => {
        let imageName = file?.[0]?.name;

        let image = {
            name: imageName,
            image: file?.[0],
            url: URL.createObjectURL(file?.[0])
        };

        setUploadedFile(image);
    };

    const handleSubmit = async (values) => {
        try {
            if (selectedRep == null) {
                return toast.error('Please select rep');
            }
            if (selectedCustomer == null) {
                return toast.error('Please select customer');
            }
            if (selectedBank == null) {
                return toast.error('Please select bank');
            }
            if (user?.role == 'cash-payments') {
                const selectedVerifiedDate = moment(verifiedAt, 'YYYY-MM-DD');
                const isValidVerifiedDate =
                    selectedVerifiedDate.isValid() &&
                    (selectedVerifiedDate.isSame(today, 'day') || selectedVerifiedDate.isSame(tomorrow, 'day'));
                if (!isValidVerifiedDate) {
                    return toast.error('Verified date must be today or tomorrow');
                }
            }
            const token = JSON.parse(localStorage.getItem('token'));

            let formdata = new FormData();
            formdata.append('user', selectedRep);
            formdata.append('customer', selectedCustomer);
            formdata.append('bank', selectedBank);
            formdata.append('amount', values?.amount);
            formdata.append('comments', values?.comments);
            formdata.append('vendor', selectedVendor);
            formdata.append('refNo', values?.refNo);
            formdata.append('createdOn', moment(createdOn).utc().toISOString());
            if (user?.role == 'cash-payments' && verifiedAt) {
                formdata.append('verifiedAt', moment(verifiedAt).endOf('day').utc().toISOString());
            }
            uploadedFile && formdata.append('slip', uploadedFile?.image, uploadedFile?.name);

            let res = await axios({
                method: 'put',
                url: `${BASE_URL}payments/${payment?._id}`,
                data: formdata,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (res?.data) {
                onClose(true);
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {isLoadingReps || isLoadingCustomer || isLoadingBank ? (
                <CircularProgress size={35} color="inherit" />
            ) : (
                <Formik
                    initialValues={{
                        ...INITIAL_FORM_STATE
                    }}
                    validationSchema={FORM_VALIDATION}
                    onSubmit={(values, { resetForm }) => {
                        setLoading(true);
                        handleSubmit(values, resetForm);
                    }}
                >
                    <Form>
                        <Grid container direction="row" spacing={2}>
                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={reps ?? []}
                                    getOptionLabel={(rep) => `${rep?.firstName} ${rep?.lastName}`}
                                    value={selectedRep && reps?.find((i) => i?.id == selectedRep)}
                                    onChange={(event, selected) => {
                                        setSelectedRep(selected?.id || null);
                                        setSelectedCustomer(null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Rep" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={selectedRep ? customer?.filter((i) => i?.user?._id == selectedRep) : []}
                                    getOptionLabel={(c) => `${c?.name}`}
                                    value={selectedCustomer ? customer?.find((i) => i?.id == selectedCustomer) : null}
                                    onChange={(event, selected) => {
                                        setSelectedCustomer(selected?.id || null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Customer" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={bank ?? []}
                                    getOptionLabel={(b) => `${b?.name}`}
                                    value={selectedBank && bank?.find((i) => i?.id == selectedBank)}
                                    onChange={(event, selected) => {
                                        setSelectedBank(selected?.id || null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Bank" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} md={4} sm={4} xs={12}>
                                <Textfield name="amount" label="Amount" />
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Textfield name="comments" label="Comments" />
                            </Grid>

                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={vendor ?? []}
                                    getOptionLabel={(v) => `${v?.name}`}
                                    value={selectedVendor && vendor?.find((i) => i?.id == selectedVendor)}
                                    onChange={(event, selected) => {
                                        setSelectedVendor(selected?.id || null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Vendor" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} md={6} sm={6} xs={12}>
                                <Textfield name="refNo" label="Ref No." />
                            </Grid>

                            <Grid item lg={6} md={6} sm={6} xs={12}>
                                <TextField
                                    size="small"
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleOnChangeUploadReport(e.target.files)}
                                />
                            </Grid>

                            {user?.role == 'cash-payments' && (
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <TextField
                                        label="Verified At"
                                        value={verifiedAt}
                                        onChange={(e) => setVerifiedAt(e.target.value)}
                                        fullWidth
                                        size="small"
                                        type="date"
                                        inputProps={{ min: today, max: tomorrow }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Box sx={styles.btnContainer}>
                            {loading ? (
                                <CircularProgress size={25} color="inherit" />
                            ) : (
                                <>
                                    {/* -------------- onClick will call the onClose function that closes the History Modal */}
                                    <Button onClick={onClose} variant="text" sx={{ color: 'red' }}>
                                        Cancel
                                    </Button>

                                    {/* -----------  change button title on the basis of history status */}
                                    <Button type="submit" variant="text">
                                        Save
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Form>
                </Formik>
            )}
        </div>
    );
};

export default memo(Edit);

const styles = {
    loadingContainer: {
        height: 400,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    btnContainer: {
        display: 'flex',
        direction: 'row',
        gap: 1,
        marginTop: 1,
        justifyContent: 'flex-end'
    }
};
